import React, { useState, useEffect } from 'react';
import { Icons } from '../components/Icons';
import { db, appId } from '../utils/firebase';
import { collection, query, where, limit, onSnapshot } from 'firebase/firestore';
import ImageLightbox from '../components/ImageLightbox';
import { safeHtml } from '../utils/safeHtml';

// 70th anniversary of the Cobalt Event: 24 Oct 1956, 12:34 (Seattle time, PDT).
const COBALT_ANNIVERSARY = new Date('2026-10-24T12:34:00-07:00').getTime();

const WhatsNext = () => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    const remaining = Math.max(0, COBALT_ANNIVERSARY - now);
    const parts = [
        ['Days', Math.floor(remaining / 86400000)],
        ['Hours', Math.floor(remaining / 3600000) % 24],
        ['Minutes', Math.floor(remaining / 60000) % 60],
        ['Seconds', Math.floor(remaining / 1000) % 60],
    ];

    return (
        <div className="w-full max-w-4xl mx-auto pt-16 px-6 relative z-10">
            <div className="flex items-center gap-4 mb-8">
                <Icons.Activity size={24} className="text-blue-500" />
                <h2 className="text-xl md:text-2xl font-black uppercase tracking-widest text-white">What's Next</h2>
                <div className="flex-1 h-px bg-gray-900 ml-4"></div>
            </div>
            <div className="bg-[#0a0a0a] border border-blue-900/40 rounded-lg p-8 md:p-12 text-center shadow-2xl">
                {remaining > 0 ? (
                    <div className="grid grid-cols-4 gap-2 md:gap-4 max-w-md mx-auto mb-8">
                        {parts.map(([label, value]) => (
                            <div key={label} className="bg-black border border-gray-800 rounded py-3 md:py-4">
                                <div className="text-2xl md:text-4xl font-black text-white font-mono tabular-nums">{String(value).padStart(2, '0')}</div>
                                <div className="text-[8px] md:text-[9px] text-gray-500 font-bold tracking-widest uppercase mt-1">{label}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-red-500 font-black tracking-[0.3em] uppercase text-sm mb-8">The hour is now</p>
                )}
                <p className="text-gray-500 font-bold tracking-[0.4em] uppercase text-xs md:text-sm">We are here to help</p>
            </div>
        </div>
    );
};

const PublicFront = ({ onBreach, onCrew }) => {
    const [announcements, setAnnouncements] = useState([]);

    useEffect(() => {
        if (!appId) return;
        // Ask only for PUBLIC posts so the database rules can allow this query without a login.
        const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'announcements'), where('visibility', '==', 'PUBLIC'), limit(200));
        const unsub = onSnapshot(q, (snap) => {
            const posts = snap.docs.map(d => ({ id: d.id, ...d.data() }))
                .filter(p => p.visibility !== 'INTERNAL')
                .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                .slice(0, 15);
            setAnnouncements(posts);
        }, (err) => console.warn('Public updates unavailable.', err));
        return () => unsub();
    }, []);

    return (
        <div className="min-h-screen flex flex-col relative bg-[#050505] text-gray-200 font-sans selection:bg-blue-500 selection:text-white overflow-y-auto custom-scrollbar">
            
            <ImageLightbox />

            {/* RICH TEXT STYLING FOR QUILL OUTPUT */}
            <style>{`
                .rich-text-public a { color: #3b82f6; text-decoration: underline; transition: color 0.2s; }
                .rich-text-public a:hover { color: #60a5fa; }
                .rich-text-public blockquote { border-left: 4px solid #333; padding-left: 1.5rem; color: #aaa; font-style: italic; margin: 1.5rem 0; }
                .rich-text-public pre { background: #111; padding: 1rem; border-radius: 6px; font-family: monospace; overflow-x: auto; font-size: 0.875rem; border: 1px solid #333; }
                .rich-text-public ul { list-style-type: disc; padding-left: 1.5rem; margin: 1rem 0; }
                .rich-text-public ol { list-style-type: decimal; padding-left: 1.5rem; margin: 1rem 0; }
                .rich-text-public li { margin-bottom: 0.5rem; }
                .rich-text-public h1 { font-size: 2.25rem; font-weight: 900; margin-top: 2rem; margin-bottom: 1rem; color: white; line-height: 1.2; letter-spacing: -0.025em; }
                .rich-text-public h2 { font-size: 1.875rem; font-weight: 800; margin-top: 2rem; margin-bottom: 1rem; color: white; line-height: 1.2; letter-spacing: -0.025em; }
                .rich-text-public h3 { font-size: 1.5rem; font-weight: 700; margin-top: 1.5rem; margin-bottom: 0.75rem; color: white; }
                .rich-text-public p { margin-bottom: 1rem; min-height: 1rem; }
                .rich-text-public strong { color: white; font-weight: 800; }
                
                /* NEW: Makes rich text images look clickable! */
                .rich-text-public img { max-width: 100%; height: auto; border-radius: 8px; margin-top: 8px; border: 1px solid #333; cursor: zoom-in; }
            `}</style>

            <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#333 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

            <div className="flex flex-col items-center justify-center p-6 text-center z-10 mt-16 mb-10 relative">
                <div className="flex flex-col items-center mb-6 cursor-pointer group select-none relative" onClick={onBreach} title="Staff login">
                    <Icons.TriangleLogo className="h-24 w-24 md:h-32 md:w-32 text-gray-700 mb-4 group-hover:scale-105 group-hover:rotate-180 group-hover:text-red-600 transition-all duration-500" />
                    <span className="text-[11px] font-bold uppercase tracking-[0.4em] text-gray-500 group-hover:text-red-500 transition-colors h-4">
                        <span className="group-hover:hidden animate-pulse">Tap to enter</span>
                        <span className="hidden group-hover:inline">Login</span>
                    </span>
                </div>
                <h1 className="text-4xl md:text-6xl font-black mb-2 uppercase tracking-tighter leading-none text-white">THE TERTIARY<br/>STRUCTURE</h1>
                <p className="text-blue-600 font-bold tracking-[0.4em] uppercase text-xs md:text-sm mt-4">Infrastructure & Development Hub</p>
                {onCrew && <button onClick={onCrew} className="mt-6 px-5 py-3 border border-gray-800 text-[11px] font-bold uppercase tracking-widest text-gray-500 hover:text-white hover:border-gray-500 transition-all rounded">Crew login: artists, musicians, writers, helpers</button>}
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 border-y border-gray-900 divide-y md:divide-y-0 md:divide-x divide-gray-900 bg-black z-10">
                <div className="p-12 bg-black text-center hover:bg-[#0a0a0a] transition-colors flex flex-col items-center justify-between min-h-[250px]">
                    <div>
                        <span className="block text-gray-800 mb-4 text-4xl font-black font-sans">I</span>
                        <h3 className="text-2xl font-bold mb-4 tracking-tight text-white">Saputo Creates</h3>
                        <div className="text-gray-500 inline-block font-bold text-[10px] tracking-[0.2em] uppercase mb-6">THE IDEA</div>
                    </div>
                    <a href="https://saputocreates.media" target="_blank" rel="noreferrer" className="mt-4 px-6 py-3 border border-gray-800 text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-white hover:border-white transition-all flex items-center gap-2">Access Portal <Icons.ExternalLink size={10} /></a>
                </div>
                <div className="p-12 bg-black text-center hover:bg-[#0a0a0a] transition-colors flex flex-col items-center justify-between min-h-[250px]">
                    <div>
                        <span className="block text-gray-800 mb-4 text-4xl font-black font-sans">II</span>
                        <h3 className="text-2xl font-bold mb-4 tracking-tight text-white">Bob's Doors</h3>
                        <div className="text-gray-500 inline-block font-bold text-[10px] tracking-[0.2em] uppercase mb-6">THE MEDIUM</div>
                    </div>
                    <a href="https://bobs-doors.com" target="_blank" rel="noreferrer" className="mt-4 px-6 py-3 border border-gray-800 text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-white hover:border-white transition-all flex items-center gap-2">Access Portal <Icons.ExternalLink size={10} /></a>
                </div>
                <div className="p-12 bg-black text-center hover:bg-[#0a0a0a] transition-colors flex flex-col items-center justify-between min-h-[250px]">
                    <div>
                        <span className="block text-gray-800 mb-4 text-4xl font-black font-sans">III</span>
                        <h3 className="text-2xl font-bold mb-4 tracking-tight text-white">The Catalyst</h3>
                        <div className="text-gray-500 inline-block font-bold text-[10px] tracking-[0.2em] uppercase mb-6">THE PEOPLE</div>
                    </div>
                    <a href="https://catalyst-art.org" target="_blank" rel="noreferrer" className="mt-4 px-6 py-3 border border-gray-800 text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-white hover:border-white transition-all flex items-center gap-2">Access Portal <Icons.ExternalLink size={10} /></a>
                </div>
            </div>

            <WhatsNext />

            {announcements.length > 0 && (
                <div className="w-full max-w-4xl mx-auto py-16 px-6 relative z-10">
                    <div className="flex items-center gap-4 mb-10">
                        <Icons.Activity size={24} className="text-blue-500" />
                        <h2 className="text-xl md:text-2xl font-black uppercase tracking-widest text-white">Public Updates Blog</h2>
                        <div className="flex-1 h-px bg-gray-900 ml-4"></div>
                    </div>
                    
                    <div className="flex flex-col gap-8">
                        {announcements.map(post => (
                            <div key={post.id} className="bg-[#0a0a0a] border border-gray-800 p-8 md:p-10 rounded-lg shadow-2xl flex flex-col">
                                <div className="flex justify-between items-center mb-6">
                                    <div className="flex gap-2">
                                        <span className={`border px-2 py-1 rounded text-[8px] font-bold tracking-widest uppercase ${post.visibility === 'INTERNAL' ? 'bg-red-900/20 text-red-400 border-red-900/50' : 'bg-green-900/20 text-green-400 border-green-900/50'}`}>
                                            {post.visibility === 'INTERNAL' ? 'CREW ONLY' : 'PUBLIC'}
                                        </span>
                                        <span className="bg-blue-900/20 text-blue-400 border border-blue-900/50 px-2 py-1 rounded text-[8px] font-bold tracking-widest uppercase">{post.category || 'NEWS'}</span>
                                    </div>
                                    <span className="text-xs text-gray-500 font-mono">{post.date}</span>
                                </div>
                                <h3 className="text-3xl md:text-4xl font-black text-white mb-6 leading-tight tracking-tighter">{post.title}</h3>
                                
                                <div 
                                    className="text-gray-300 text-base md:text-lg leading-relaxed font-sans mb-8 rich-text-public"
                                    dangerouslySetInnerHTML={{ __html: safeHtml(post.text) }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <footer className="bg-black text-gray-600 py-10 text-center text-[10px] font-bold tracking-widest uppercase border-t border-gray-900 mt-auto z-10">
                <p className="mb-2"><a href="mailto:nick@saputocreates.com" className="text-gray-400 hover:text-white transition-colors">nick@saputocreates.com</a></p>
                <p className="opacity-50">&copy; {new Date().getFullYear()} SAPUTOCREATIVE {'//'} T3S HUB v1.6.0</p>
            </footer>
        </div>
    );
};

export default PublicFront;